// src/components/SubmitTime/hooks/useCustomFields.ts
// Custom hook for managing custom fields
// Migrated from SubmitTime.js custom field methods

import { useState, useCallback } from 'react';
import type { CustomFieldConfig, CustomFieldResponse, SubmitTimeConfiguration } from '../types';

export interface ProcessCustomFieldOptions {
  hasPhases?: boolean;
  showPhases?: boolean;
  typesubmittime?: 1 | 2;
}

export interface CustomFieldsHelpers {
  addedConfigurations: CustomFieldConfig[] | null;
  setAddedConfigurations: (configs: CustomFieldConfig[] | null) => void;
  backupCustomFields: CustomFieldConfig[];
  setBackupCustomFields: (configs: CustomFieldConfig[]) => void;
  processCustomFieldResponse: (
    response: CustomFieldResponse[],
    configurations: SubmitTimeConfiguration[],
    layerId: number,
    configValueId?: number,
    options?: ProcessCustomFieldOptions
  ) => CustomFieldConfig[];
  getEstAndRem: (
    response: CustomFieldResponse[],
    layerId: number,
    estimatedName: string,
    remainingName: string,
    actualName: string,
    phasesName?: string
  ) => {
    estimated: string;
    remaining: string;
    actual: string;
    estimatedId: number | undefined;
    remainingId: number | undefined;
    actualId: number | undefined;
  };
  handleDynamicInputChange: (customFieldId: number, value: string | number) => void;
  updateCustomField: (customFieldId: number, value: string | number) => void;
}

export function useCustomFields(): CustomFieldsHelpers
{
  const [addedConfigurations, setAddedConfigurations] = useState<CustomFieldConfig[] | null>(null);
  const [backupCustomFields, setBackupCustomFields] = useState<CustomFieldConfig[]>([]);

  // Mirrors legacy setAddedConfigurations filter logic exactly.
  //
  // When phases are active (typesubmittime=1 + HasPhases, or typesubmittime=2 + showPhases):
  //   - Include configs where ConfigValueID+LayerId match AND ItemType="Phases"
  //   - OR include configs where ItemType="Phases" AND LayerId matches (any ConfigValueID)
  //   → In practice: any config with ItemType="Phases" matching the selected LayerId
  //
  // When phases are NOT active:
  //   - Include configs where ConfigValueID+LayerId match (null = applies to all)
  //
  // Field matching uses CustomField name (legacy behavior), with CustomFieldId as fallback.
  const processCustomFieldResponse = useCallback(
    (
      response: CustomFieldResponse[],
      configurations: SubmitTimeConfiguration[],
      layerId: number,
      configValueId?: number,
      options?: ProcessCustomFieldOptions,
    ): CustomFieldConfig[] =>
    {
      const { hasPhases = false, showPhases = false, typesubmittime } = options || {};

      const isPhaseMode =
        (typesubmittime === 1 && hasPhases) || (typesubmittime === 2 && showPhases);

      const layerData = Array.isArray(response)
        ? response.find(r => r.LayerId === layerId)
        : undefined;

      if (!layerData || !layerData.CustomFields) return [];

      const relevantConfigs = configurations.filter(config =>
      {
        if (config.CustomField === 'Remaining' || config.CustomField === 'Estimated') return false;

        const layerMatch =
          config.LayerId === layerId ||
          config.LayerId === null ||
          config.LayerId === undefined ||
          config.LayerId === 0;

        if (!layerMatch) return false;

        if (isPhaseMode)
        {
          // Phases path: only ItemType="Phases" configs, ConfigValueID is irrelevant
          return config.ItemType === 'Phases';
        }

        // Normal path: ConfigValueID must match (null/0/undefined = applies to all)
        return (
          configValueId === undefined ||
          !config.ConfigValueID ||
          config.ConfigValueID === configValueId
        );
      });

      if (relevantConfigs.length === 0) return [];

      // Match fields by name first (legacy behavior), then by CustomFieldId as fallback
      const processedFields: CustomFieldConfig[] = [];
      for (const config of relevantConfigs)
      {
        const field = layerData.CustomFields.find(
          f =>
            (config.CustomField && f.Name === config.CustomField) ||
            f.CustomFieldId === config.CustomFieldId,
        );
        if (field)
        {
          processedFields.push({
            ...field,
            Editable: config.Editable,
            Required: config.Required,
          });
        }
      }

      return processedFields;
    },
    [],
  );

  // Extract Estimated, Remaining, and Actual fields
  const getEstAndRem = useCallback(
    (
      response: CustomFieldResponse[],
      layerId: number,
      estimatedName: string,
      remainingName: string,
      actualName: string,
      _phasesName?: string,
    ) =>
    {
      const layerData = Array.isArray(response) ? response.find(r => r.LayerId === layerId) : undefined;
      if (!layerData || !layerData.CustomFields)
      {
        return {
          estimated: '',
          remaining: '',
          actual: '',
          estimatedId: undefined,
          remainingId: undefined,
          actualId: undefined,
        };
      }

      const estimatedField = layerData.CustomFields.find(f => f.Name === estimatedName);
      const remainingField = layerData.CustomFields.find(f => f.Name === remainingName);
      const actualField = layerData.CustomFields.find(f => f.Name === actualName);

      return {
        estimated: estimatedField?.Value?.toString() || '',
        remaining: remainingField?.Value?.toString() || '',
        actual: actualField?.Value?.toString() || '',
        estimatedId: estimatedField?.CustomFieldId,
        remainingId: remainingField?.CustomFieldId,
        actualId: actualField?.CustomFieldId,
      };
    },
    [],
  );

  // Handle dynamic input change
  const handleDynamicInputChange = useCallback((customFieldId: number, value: string | number) =>
  {
    setAddedConfigurations(prev =>
    {
      if (!prev) return prev;

      return prev.map(field =>
      {
        if (field.CustomFieldId === customFieldId)
        {
          return {
            ...field,
            Value: value,
          };
        }
        return field;
      });
    });
  }, []);

  // Update a specific custom field value
  const updateCustomField = useCallback((customFieldId: number, value: string | number) =>
  {
    setAddedConfigurations(prev =>
    {
      if (!prev) return prev;

      return prev.map(field =>
      {
        if (field.CustomFieldId === customFieldId)
        {
          return {
            ...field,
            Value: value,
          };
        }
        return field;
      });
    });
  }, []);

  return {
    addedConfigurations,
    setAddedConfigurations,
    backupCustomFields,
    setBackupCustomFields,
    processCustomFieldResponse,
    getEstAndRem,
    handleDynamicInputChange,
    updateCustomField,
  };
}
